import React, { useState, useEffect } from 'react';
import { X, Save, Tag, CreditCard, Hash, Building } from 'lucide-react';
import { TagBlac } from '../../interfaces/TagBlacInterface';
import { toast } from 'react-toastify';
import { API } from '../../global/global';

interface Plataforma {
  id: number;
  nombre: string;
}

interface TagEditModalProps {
  tag: TagBlac;
  isOpen: boolean;
  onClose: () => void;
  onSave: (tagId: number, plataformaId: number) => void;
}

export const TagEditModal: React.FC<TagEditModalProps> = ({ tag, isOpen, onClose, onSave }) => {
  const [selectedPlataforma, setSelectedPlataforma] = useState<number>(tag.plataforma_id || 0);
  const [plataformas, setPlataformas] = useState<Plataforma[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadPlataformas();
      setSelectedPlataforma(tag.plataforma_id || 0);
    }
  }, [isOpen, tag]);

  const loadPlataformas = async () => {
    try {
      // Cargar plataformas desde la API
      const response = await fetch(`${API}/plataformas`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Error al cargar plataformas');
      }
      
      const data = await response.json();
      // Asegurar que data sea un array
      const plataformasArray = Array.isArray(data) ? data : (data.data ? data.data : []);
      setPlataformas(plataformasArray);
    } catch (error) {
      console.error('Error cargando plataformas:', error);
      setPlataformas([]);
      toast.error('Error al cargar las plataformas');
    }
  };

  const handleSave = async () => {
    if (selectedPlataforma === 0) {
      toast.error('Debe seleccionar una plataforma');
      return;
    }

    setLoading(true);
    try {
      await onSave(tag.id, selectedPlataforma);
      toast.success('Tag actualizada correctamente');
      onClose();
    } catch (error) {
      console.error('Error actualizando tag:', error);
      toast.error('Error al actualizar la tag');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: #1a1a1a;
          border-radius: 12px;
          width: 90%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .modal-header h3 {
          margin: 0;
          color: white;
          font-size: 1.25rem;
        }

        .modal-close {
          background: none;
          border: none;
          color: rgba(255, 255, 255, 0.6);
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
        }

        .modal-close:hover {
          background: rgba(255, 255, 255, 0.1);
          color: white;
        }

        .modal-body {
          padding: 1.5rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          color: rgba(255, 255, 255, 0.8);
          font-weight: 500;
        }

        .form-control {
          width: 100%;
          padding: 12px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          color: white;
          font-size: 14px;
        }

        .form-control:focus {
          outline: none;
          border-color: #007AFF;
          background: rgba(255, 255, 255, 0.15);
        }

        .form-control option {
          background: #1a1a1a;
          color: white;
          padding: 8px;
        }

        .form-control select {
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          padding: 1.5rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .btn {
          padding: 10px 20px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s;
        }

        .btn-secondary {
          background: rgba(255, 255, 255, 0.1);
          color: rgba(255, 255, 255, 0.8);
        }

        .btn-secondary:hover {
          background: rgba(255, 255, 255, 0.2);
          color: white;
        }

        .btn-primary {
          background: #007AFF;
          color: white;
        }

        .btn-primary:hover {
          background: #0056CC;
        }

        .btn-primary:disabled {
          background: rgba(0, 122, 255, 0.3);
          cursor: not-allowed;
        }
      `}</style>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h3>Editar Tag - {tag.name_device}</h3>
          <button onClick={onClose} className="modal-close">
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          <div className="form-group">
            <label>Información de la Tag</label>
            <div style={{ 
              background: 'linear-gradient(135deg, rgba(0, 122, 255, 0.1), rgba(0, 122, 255, 0.05))', 
              padding: '1.5rem', 
              borderRadius: '12px',
              marginBottom: '1.5rem',
              border: '1px solid rgba(0, 122, 255, 0.2)'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '1rem',
                paddingBottom: '0.5rem',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <Tag size={20} style={{ color: '#007AFF', marginRight: '8px' }} />
                <span style={{ color: '#007AFF', fontWeight: '600', fontSize: '1.1rem' }}>
                  Información de la Tag
                </span>
              </div>
              
              <div style={{ display: 'grid', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Tag size={16} style={{ color: 'rgba(255, 255, 255, 0.6)', marginRight: '12px', minWidth: '16px' }} />
                  <span style={{ color: 'rgba(255, 255, 255, 0.7)', minWidth: '80px' }}>Nombre:</span>
                  <span style={{ color: 'white', fontWeight: '500' }}>{tag.name_device}</span>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <CreditCard size={16} style={{ color: 'rgba(255, 255, 255, 0.6)', marginRight: '12px', minWidth: '16px' }} />
                  <span style={{ color: 'rgba(255, 255, 255, 0.7)', minWidth: '80px' }}>Nombre:</span>
                  <span style={{ color: 'white', fontWeight: '500' }}>{tag.plate}</span>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Hash size={16} style={{ color: 'rgba(255, 255, 255, 0.6)', marginRight: '12px', minWidth: '16px' }} />
                  <span style={{ color: 'rgba(255, 255, 255, 0.7)', minWidth: '80px' }}>ID:</span>
                  <span style={{ color: 'white', fontWeight: '500' }}>{tag.id}</span>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Building size={16} style={{ color: 'rgba(255, 255, 255, 0.6)', marginRight: '12px', minWidth: '16px' }} />
                  <span style={{ color: 'rgba(255, 255, 255, 0.7)', minWidth: '80px' }}>Plataforma:</span>
                  <span style={{ 
                    color: tag.plataforma_id ? '#34C759' : '#FF9500', 
                    fontWeight: '500',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    background: tag.plataforma_id ? 'rgba(52, 199, 89, 0.1)' : 'rgba(255, 149, 0, 0.1)',
                    fontSize: '0.9rem'
                  }}>
                    {tag.plataforma_id ? `ID: ${tag.plataforma_id}` : 'Sin asignar'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="plataforma">Asignar a Plataforma *</label>
            <select
              id="plataforma"
              value={selectedPlataforma}
              onChange={(e) => setSelectedPlataforma(Number(e.target.value))}
              className="form-control"
              style={{
                width: '100%',
                padding: '12px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: 'white'
              }}
            >
              <option value={0}>Seleccionar plataforma...</option>
              {plataformas.map((plataforma) => (
                <option key={plataforma.id} value={plataforma.id}>
                  {plataforma.nombre}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">
            Cancelar
          </button>
          <button 
            onClick={handleSave} 
            className="btn btn-primary"
            disabled={loading || selectedPlataforma === 0}
          >
            <Save size={16} />
            {loading ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
        </div>
      </div>
    </>
  );
};
